/**
 * The four buttons.
 *
 * ---------------------------------------------------------------------------
 * WHY THEY ARE NOT CALLED RESET, RESTART AND STOP
 * ---------------------------------------------------------------------------
 * Those were the working names and no two people read them the same way. Ask
 * five people what "reset" does to a countdown and you will get "puts it back
 * how it was" as often as "starts a new one" - and here it meant the second,
 * while "restart" meant the first. Two words that both mean "start again",
 * assigned to opposite actions, is a coin toss dressed up as a label.
 *
 * So each button is named for its OUTCOME rather than its operation:
 *
 *   Back to the original  - the countdown this site is about, restored.
 *   Start a new six years - a fresh run beginning now.
 *   Skip to zero          - it is over.
 *   Run forever           - there is no end to reach.
 *
 * ---------------------------------------------------------------------------
 * WHY THE SYMBOL IS NOT THE WHOLE BUTTON
 * ---------------------------------------------------------------------------
 * A tooltip is a `title` attribute, and a `title` attribute does not exist on
 * a touchscreen - there is no hover, so on the device most likely to be used
 * here the explanation is simply absent. The visible text label is therefore
 * always present, and the tooltip is the longer sentence layered on top for
 * people who can hover. The icon carries the meaning at a glance; the words
 * carry it the first time.
 *
 * ---------------------------------------------------------------------------
 * WHY THREE OF THEM ASK TWICE
 * ---------------------------------------------------------------------------
 * These buttons write shared state: a press changes what every other device is
 * showing, and three of the four destroy a countdown that cannot be recovered
 * by any other button. A pocket press or a mis-tap on a phone should not be
 * able to do that. "Back to the original" is exempt because it is idempotent
 * and is itself the undo for the other three - putting a confirm step in front
 * of the escape hatch is how you get people stuck.
 *
 * Arming rather than `confirm()`: a native dialog is blocked in some embedded
 * browsers, steals focus, and cannot be styled, and its Cancel button is the
 * one people hit by reflex.
 */

import type { Mode } from './shared/countdown.ts'

const ARM_TIMEOUT_MS = 5_000

export interface Controls {
  /** Disarms everything - used when the state changes underneath us. */
  reset(): void
}

export function mountControls(
  root: HTMLElement,
  handlers: { onPress: (mode: Mode) => void; announce: (message: string) => void }
): Controls {
  const buttons = Array.from(root.querySelectorAll<HTMLButtonElement>('button[data-mode]'))
  let armed: HTMLButtonElement | null = null
  let armTimer: number | undefined

  const labelOf = (button: HTMLButtonElement): HTMLElement =>
    button.querySelector<HTMLElement>('.control-label')!

  const disarm = (): void => {
    window.clearTimeout(armTimer)
    if (!armed) return
    const button = armed
    armed = null
    button.dataset.armed = 'false'
    labelOf(button).textContent = button.dataset.label ?? ''
    button.setAttribute('aria-label', button.dataset.description ?? '')
  }

  const arm = (button: HTMLButtonElement): void => {
    disarm()
    armed = button
    button.dataset.armed = 'true'
    labelOf(button).textContent = 'Tap again to confirm'
    const message = `${button.dataset.label}: tap again to confirm. This changes the countdown on every device.`
    button.setAttribute('aria-label', message)
    handlers.announce(message)
    armTimer = window.setTimeout(() => {
      disarm()
      handlers.announce('Cancelled. Nothing changed.')
    }, ARM_TIMEOUT_MS)
  }

  for (const button of buttons) {
    button.disabled = false
    button.addEventListener('click', () => {
      const mode = button.dataset.mode as Mode
      const needsConfirm = button.dataset.confirm === 'true'
      if (needsConfirm && armed !== button) {
        arm(button)
        return
      }
      disarm()
      handlers.onPress(mode)
      handlers.announce(button.dataset.done ?? 'Done.')
    })
    // Losing focus is a good enough signal that the confirm was abandoned;
    // leaving a primed button behind is how the *next* visit to that button
    // fires immediately.
    button.addEventListener('blur', () => {
      if (armed === button) disarm()
    })
  }

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && armed) {
      disarm()
      handlers.announce('Cancelled. Nothing changed.')
    }
  })

  return { reset: disarm }
}
